import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ArrowDownCircle, ArrowUpCircle, History, RefreshCw, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  WarehouseItem,
  WarehouseMovement,
  WarehouseMovementType,
  MOVEMENT_TYPE_LABELS,
  useWarehouseMovements,
} from '@/hooks/warehouse';

interface MovementHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  warehouseItem: WarehouseItem | null;
}

const getMovementIcon = (type: WarehouseMovementType) => {
  switch (type) {
    case 'entrada_compra':
      return <ArrowDownCircle className="h-4 w-4 text-success" />;
    case 'salida_propiedad':
      return <ArrowUpCircle className="h-4 w-4 text-destructive" />;
    case 'ajuste_merma':
      return <RefreshCw className="h-4 w-4 text-warning" />;
    case 'devolucion':
      return <RotateCcw className="h-4 w-4 text-info" />;
    default:
      return <History className="h-4 w-4" />;
  }
};

const getMovementVariant = (type: WarehouseMovementType): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (type) {
    case 'entrada_compra':
    case 'devolucion':
      return 'default';
    case 'salida_propiedad':
      return 'destructive';
    case 'ajuste_merma':
      return 'secondary';
    default:
      return 'outline';
  }
};

const MovementHistoryModal = ({ isOpen, onClose, warehouseItem }: MovementHistoryModalProps) => {
  const { data: movements = [], isLoading } = useWarehouseMovements(warehouseItem?.id || null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value);
  };

  const isAddition = (type: WarehouseMovementType) => 
    type === 'entrada_compra' || type === 'devolucion';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Historial de Movimientos
          </DialogTitle>
          {warehouseItem?.product && (
            <p className="text-sm text-muted-foreground">
              {warehouseItem.product.name}
            </p>
          )}
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Cargando historial...
            </div>
          ) : movements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No hay movimientos registrados
            </div>
          ) : (
            <div className="space-y-4">
              {movements.map((movement) => (
                <div
                  key={movement.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                >
                  <div className="mt-0.5">
                    {getMovementIcon(movement.movement_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant={getMovementVariant(movement.movement_type)}>
                        {MOVEMENT_TYPE_LABELS[movement.movement_type]}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(movement.created_at), "dd MMM yyyy, HH:mm", { locale: es })}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <span className={isAddition(movement.movement_type) ? 'text-success' : 'text-destructive'}>
                        {isAddition(movement.movement_type) ? '+' : '-'}{movement.quantity} unidades
                      </span>
                      <span className="text-muted-foreground">
                        @ {formatCurrency(movement.unit_price)}
                      </span>
                    </div>
                    {movement.notes && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {movement.notes}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default MovementHistoryModal;
